import React, { useEffect, useState, Component } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Target, Terminal, Database, Server, Cpu, Shield, Zap, LayoutDashboard, FileText, CheckCircle2, Code2, Workflow, Rocket } from 'lucide-react';
// Custom hook for scroll spy
const useScrollSpy = (ids: string[], offset: number = 100) => {
  const [activeId, setActiveId] = useState('');
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveId(entry.target.id);
        }
      });
    }, {
      rootMargin: `-${offset}px 0px -80% 0px`
    });
    ids.forEach(id => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });
    return () => observer.disconnect();
  }, [ids, offset]);
  return activeId;
};
const SECTIONS = [{
  id: 'welcome',
  title: 'Welcome & Opportunity',
  icon: Rocket
}, {
  id: 'overview',
  title: 'Product Overview',
  icon: Target
}, {
  id: 'prototype',
  title: 'Current Prototype',
  icon: LayoutDashboard
}, {
  id: 'architecture',
  title: 'Technical Architecture',
  icon: Server
}, {
  id: 'roadmap',
  title: 'Feature Roadmap',
  icon: Workflow
}, {
  id: 'schema',
  title: 'Database Schema',
  icon: Database
}, {
  id: 'ai',
  title: 'AI Integration Details',
  icon: Cpu
}, {
  id: 'api',
  title: 'API Design',
  icon: Terminal
}, {
  id: 'deployment',
  title: 'Deployment & DevOps',
  icon: Zap
}, {
  id: 'workflow',
  title: 'Development Workflow',
  icon: Code2
}, {
  id: 'metrics',
  title: 'Success Metrics',
  icon: Target
}, {
  id: 'getting-started',
  title: 'Getting Started',
  icon: CheckCircle2
}];
// Reusable components for the document
const SectionHeader = ({
  num,
  title
}: {
  num: string;
  title: string;
}) => <div className="flex items-baseline gap-4 mb-8 border-b border-stone-800 pb-4" data-id="element-1283">
    <span className="font-serif text-3xl text-red-600" data-id="element-1284">{num}</span>
    <h2 className="font-serif text-3xl text-stone-50" data-id="element-1285">{title}</h2>
  </div>;
const Code = ({
  children
}: {
  children: React.ReactNode;
}) => <code className="font-mono text-sm bg-stone-900 px-1.5 py-0.5 text-stone-300 rounded-sm" data-id="element-1286">
    {children}
  </code>;
const Callout = ({
  children
}: {
  children: React.ReactNode;
}) => <div className="border-l-4 border-red-600 bg-stone-900/50 p-6 my-6 text-stone-300 leading-relaxed" data-id="element-1287">
    {children}
  </div>;
const Card = ({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) => <div className="border border-stone-800 bg-stone-900/30 p-6" data-id="element-1288">
    <h4 className="font-medium text-stone-50 mb-3" data-id="element-1289">{title}</h4>
    <div className="text-stone-400 text-sm leading-relaxed space-y-2" data-id="element-1290">
      {children}
    </div>
  </div>;
export function DevPlan() {
  const activeId = useScrollSpy(SECTIONS.map(s => s.id));
  const scrollTo = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const y = element.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen bg-stone-950 text-stone-300 font-sans selection:bg-red-600/30" data-id="element-1291">
      {/* Top Bar */}
      <div className="fixed top-0 left-0 right-0 h-16 bg-stone-950/80 backdrop-blur-md border-b border-stone-800 z-50 flex items-center px-6" data-id="element-1292">
        <Link to="/" className="flex items-center text-stone-400 hover:text-stone-50 transition-colors font-mono text-xs uppercase tracking-widest" data-id="element-1293">
          <ArrowLeft className="w-4 h-4 mr-2" data-id="element-1294" />
          Back to site
        </Link>
      </div>

      <div className="max-w-7xl mx-auto px-6 pt-24 pb-32 flex items-start gap-16" data-id="element-1295">
        {/* Left Sidebar (TOC) */}
        <aside className="hidden lg:block w-64 flex-shrink-0 sticky top-24 max-h-[calc(100vh-8rem)] overflow-y-auto custom-scrollbar pr-4" data-id="element-1296">
          <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-6" data-id="element-1297">
            Table of Contents
          </p>
          <nav className="space-y-1" data-id="element-1298">
            {SECTIONS.map((section, idx) => {
            const isActive = activeId === section.id;
            const Icon = section.icon;
            return <button key={section.id} onClick={() => scrollTo(section.id)} className={`w-full flex items-center gap-3 px-3 py-2 text-sm text-left transition-colors rounded-sm ${isActive ? 'bg-stone-900 text-stone-50' : 'text-stone-500 hover:text-stone-300 hover:bg-stone-900/50'}`} data-id="element-1299">
                  <span className={`font-mono text-[10px] ${isActive ? 'text-red-500' : 'text-stone-600'}`} data-id="element-1300">
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                  <span className="truncate" data-id="element-1301">{section.title}</span>
                </button>;
          })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 max-w-4xl" data-id="element-1302">
          {/* Header */}
          <header className="mb-20" data-id="element-1303">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-red-950/30 border border-red-900/50 text-red-500 font-mono text-xs uppercase tracking-widest mb-8" data-id="element-1304">
              <Shield className="w-3.5 h-3.5" data-id="element-1305" />
              Confidential — For Development Team Only
            </div>
            <h1 className="font-serif text-5xl md:text-6xl tracking-tight text-stone-50 leading-[1.1] mb-6" data-id="element-1306">
              BlockQuote.ai — Product Requirements Document & Development Plan
            </h1>
            <p className="text-xl text-stone-400 leading-relaxed" data-id="element-1307">
              Everything you need to build, deploy, and scale the infrastructure
              layer for group travel.
            </p>
          </header>

          <div className="space-y-24" data-id="element-1308">
            {/* Section 1 */}
            <section id="welcome" className="scroll-mt-24" data-id="element-1309">
              <SectionHeader num="01" title="Welcome to BlockQuote.ai" data-id="element-1310" />
              <div className="space-y-6 text-lg" data-id="element-1311">
                <p data-id="element-1312">
                  Welcome to the team. This is a greenfield SaaS platform for
                  the group travel industry.
                </p>
                <p data-id="element-1313">
                  BlockQuote.ai replaces the broken email-based quoting process
                  for resort group business (weddings, corporate retreats,
                  incentive trips) with instant, AI-powered quoting
                  infrastructure.
                </p>
                <p data-id="element-1314">
                  The prototype and marketing site are already built (what
                  you're looking at). Your job is to take this from prototype to
                  production.
                </p>
                <p data-id="element-1315">
                  <strong data-id="element-1316">Tech philosophy:</strong> We leverage AI-assisted
                  development (Claude Code, Cursor, etc.) for speed. We deploy
                  on Vercel. We move fast but build properly.
                </p>
                <Callout data-id="element-1317">
                  You will be working directly with the founder. This is a
                  high-impact, high-autonomy role.
                </Callout>
              </div>
            </section>

            {/* Section 2 */}
            <section id="overview" className="scroll-mt-24" data-id="element-1318">
              <SectionHeader num="02" title="What BlockQuote.ai Does" data-id="element-1319" />
              <ul className="space-y-4 list-disc list-inside ml-4 text-lg" data-id="element-1320">
                <li data-id="element-1321">
                  <strong data-id="element-1322">Core value prop:</strong> Instant group quotes,
                  one-click holds, automated contracts, direct resort pricing.
                </li>
                <li data-id="element-1323">
                  <strong data-id="element-1324">Two user types:</strong> Resort Groups (supply side)
                  and Travel Agencies (demand side).
                </li>
                <li data-id="element-1325">
                  The platform eliminates wholesale leakage (15-25% margin lost
                  to intermediaries) by enabling direct distribution.
                </li>
                <li data-id="element-1326">
                  <strong data-id="element-1327">Business model:</strong> 5% of verified net savings
                  (savings-share). We only earn when the resort saves money.
                </li>
                <li data-id="element-1328">
                  <strong data-id="element-1329">Key proof point:</strong> Moon Palace / Palace Resorts
                  proved this model internally. DestaWed became their #1 agency
                  using instant quoting. BlockQuote packages this for every
                  resort.
                </li>
              </ul>
            </section>

            {/* Section 3 */}
            <section id="prototype" className="scroll-mt-24" data-id="element-1330">
              <SectionHeader num="03" title="What's Already Built (Prototype)" data-id="element-1331" />
              <p className="mb-6 text-lg" data-id="element-1332">
                The following pages and components are already built in the
                current repository:
              </p>

              <h3 className="font-serif text-2xl text-stone-50 mb-4 mt-8" data-id="element-1333">
                Pages
              </h3>
              <div className="space-y-3" data-id="element-1334">
                <p data-id="element-1335">
                  <Code data-id="element-1336">/</Code> (LandingPage) — Full marketing site with Hero,
                  Problem, Solution, Case Study, Features, For Resorts, For
                  Agencies, Agency Signup, Business Model, AI Capabilities,
                  Roadmap, CTA sections
                </p>
                <p data-id="element-1337">
                  <Code data-id="element-1338">/deck</Code> (PitchDeck) — Investor pitch deck,
                  slide-based presentation with 15+ slides
                </p>
                <p data-id="element-1339">
                  <Code data-id="element-1340">/sales</Code> (ResortSalesDeck) — Resort-focused sales
                  deck, 11 slides with financial models
                </p>
                <p data-id="element-1341">
                  <Code data-id="element-1342">/app</Code> (Dashboard) — Resort dashboard prototype
                  with Overview, Quotes, Holds, Analytics tabs, quote detail
                  view
                </p>
                <p data-id="element-1343">
                  <Code data-id="element-1344">/agency</Code> (AgencyPortal) — Agency portal with
                  property browser, booking builder, my bookings views
                </p>
                <p data-id="element-1345">
                  <Code data-id="element-1346">/onboarding/lomas</Code> (LomasOnboarding) — 8-step
                  client onboarding wizard for Lomas Group pilot
                </p>
                <p data-id="element-1347">
                  <Code data-id="element-1348">/dev-plan</Code> (this page)
                </p>
              </div>

              <h3 className="font-serif text-2xl text-stone-50 mb-4 mt-8" data-id="element-1349">
                Component Inventory
              </h3>
              <div className="space-y-3" data-id="element-1350">
                <p data-id="element-1351">
                  <strong data-id="element-1352">Landing:</strong> Navbar, Hero, Problem, Solution,
                  CaseStudy, Features, ForResorts, ForAgencies, AgencySignup,
                  BusinessModel, AICapabilities, Roadmap, CTASection, Footer
                </p>
                <p data-id="element-1353">
                  <strong data-id="element-1354">Dashboard:</strong> Sidebar, OverviewTab, QuotesTab,
                  QuoteDetail, HoldsTab, AnalyticsTab
                </p>
                <p data-id="element-1355">
                  <strong data-id="element-1356">Agency:</strong> AgencyNav, PropertyBrowser,
                  BookingBuilder, MyBookings
                </p>
              </div>

              <Callout data-id="element-1357">
                <strong data-id="element-1358">Note:</strong> All current components are frontend-only
                with mock data. The production build requires a real backend,
                database, authentication, and AI integration.
              </Callout>
            </section>

            {/* Section 4 */}
            <section id="architecture" className="scroll-mt-24" data-id="element-1359">
              <SectionHeader num="04" title="Production Tech Stack" data-id="element-1360" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1361">
                <Card title="Frontend" data-id="element-1362">
                  <ul className="list-disc list-inside space-y-1" data-id="element-1363">
                    <li data-id="element-1364">React 18+ with TypeScript</li>
                    <li data-id="element-1365">Tailwind CSS for styling</li>
                    <li data-id="element-1366">Framer Motion for animations</li>
                    <li data-id="element-1367">React Router for navigation</li>
                    <li data-id="element-1368">
                      Deployment: Vercel (with preview deployments per PR)
                    </li>
                  </ul>
                </Card>
                <Card title="Backend" data-id="element-1369">
                  <ul className="list-disc list-inside space-y-1" data-id="element-1370">
                    <li data-id="element-1371">
                      Next.js API Routes OR separate Node.js/Express API
                      (developer's recommendation welcome)
                    </li>
                    <li data-id="element-1372">
                      PostgreSQL (via Supabase or Neon) for primary database
                    </li>
                    <li data-id="element-1373">
                      Redis for caching (quote locks, rate limiting, session
                      management)
                    </li>
                    <li data-id="element-1374">Prisma ORM for database access</li>
                  </ul>
                </Card>
                <Card title="Authentication & Authorization" data-id="element-1375">
                  <ul className="list-disc list-inside space-y-1" data-id="element-1376">
                    <li data-id="element-1377">Clerk or NextAuth for authentication</li>
                    <li data-id="element-1378">
                      Role-based access: Resort Admin, Resort Staff, Agency
                      Admin, Agency Agent, Super Admin
                    </li>
                    <li data-id="element-1379">
                      Multi-tenant architecture: each resort group is an
                      isolated tenant
                    </li>
                  </ul>
                </Card>
                <Card title="AI & Intelligence Layer" data-id="element-1380">
                  <ul className="list-disc list-inside space-y-1" data-id="element-1381">
                    <li data-id="element-1382">
                      Anthropic Claude API (claude-sonnet) for: RFP
                      parsing/intake automation, negotiation copilot
                      suggestions, document Q&A via RAG
                    </li>
                    <li data-id="element-1383">
                      RAG Pipeline: LangChain or LlamaIndex for document
                      ingestion, Pinecone or Supabase pgvector for vector
                      storage, PDF/Doc parsing via Unstructured or LlamaParse
                    </li>
                    <li data-id="element-1384">
                      RateBrain (pricing intelligence): Competitive rate
                      monitoring, demand pattern analysis, dynamic pricing
                      recommendations (Phase 2+)
                    </li>
                  </ul>
                </Card>
                <Card title="Infrastructure" data-id="element-1385">
                  <ul className="list-disc list-inside space-y-1" data-id="element-1386">
                    <li data-id="element-1387">Vercel for frontend + serverless functions</li>
                    <li data-id="element-1388">
                      Supabase for database + auth + storage + vector search (or
                      equivalent)
                    </li>
                    <li data-id="element-1389">Resend or SendGrid for transactional email</li>
                    <li data-id="element-1390">Stripe for payment processing (future)</li>
                    <li data-id="element-1391">Upstash Redis for caching and rate limiting</li>
                  </ul>
                </Card>
                <Card title="Development Tools" data-id="element-1392">
                  <ul className="list-disc list-inside space-y-1" data-id="element-1393">
                    <li data-id="element-1394">Claude Code / Cursor for AI-assisted development</li>
                    <li data-id="element-1395">GitHub for version control</li>
                    <li data-id="element-1396">Linear or GitHub Issues for project management</li>
                    <li data-id="element-1397">Vitest for testing</li>
                  </ul>
                </Card>
              </div>
            </section>

            {/* Section 5 */}
            <section id="roadmap" className="scroll-mt-24" data-id="element-1398">
              <SectionHeader num="05" title="Feature Roadmap — What to Build" data-id="element-1399" />

              <div className="space-y-8" data-id="element-1400">
                <div data-id="element-1401">
                  <h3 className="font-serif text-2xl text-stone-50 mb-4 flex items-center gap-3" data-id="element-1402">
                    <span className="font-mono text-sm bg-stone-800 px-2 py-1 rounded-sm" data-id="element-1403">
                      Phase 1
                    </span>
                    MVP (Weeks 1-6)
                  </h3>
                  <ol className="list-decimal list-inside space-y-2 ml-2" data-id="element-1404">
                    <li data-id="element-1405">
                      Authentication & multi-tenant setup (resort + agency
                      accounts)
                    </li>
                    <li data-id="element-1406">
                      Resort onboarding flow (property setup, rate sheet upload,
                      policy configuration)
                    </li>
                    <li data-id="element-1407">
                      Quote engine — instant quote generation from agency
                      requests (rooms, dates, events → Good/Better/Best
                      packages)
                    </li>
                    <li data-id="element-1408">
                      Hold system — one-click inventory holds with auto-expiry
                      timers
                    </li>
                    <li data-id="element-1409">
                      Agency portal — property browsing, quote requests, hold
                      management
                    </li>
                    <li data-id="element-1410">
                      Resort dashboard — quote management, hold tracking, basic
                      analytics
                    </li>
                    <li data-id="element-1411">
                      Email notifications (quote sent, hold expiring, deposit
                      due)
                    </li>
                    <li data-id="element-1412">
                      Proposal PDF generation (branded, professional proposals)
                    </li>
                  </ol>
                </div>

                <div data-id="element-1413">
                  <h3 className="font-serif text-2xl text-stone-50 mb-4 flex items-center gap-3" data-id="element-1414">
                    <span className="font-mono text-sm bg-stone-800 px-2 py-1 rounded-sm" data-id="element-1415">
                      Phase 2
                    </span>
                    Intelligence (Weeks 7-12)
                  </h3>
                  <ol className="list-decimal list-inside space-y-2 ml-2" data-id="element-1416">
                    <li data-id="element-1417">
                      AI intake automation — parse email RFPs into structured
                      quote requests using Claude
                    </li>
                    <li data-id="element-1418">
                      RAG pipeline — ingest resort SOPs, policies, rate sheets
                      for AI-powered Q&A
                    </li>
                    <li data-id="element-1419">
                      Pricing guardrails — rate floor enforcement, concession
                      limit checks, blackout date validation
                    </li>
                    <li data-id="element-1420">
                      RateBrain v1 — competitive set monitoring, basic demand
                      signals
                    </li>
                    <li data-id="element-1421">
                      Analytics dashboard — conversion rates, quote velocity,
                      leakage tracking
                    </li>
                    <li data-id="element-1422">
                      Contract workflow — digital contracts with e-signature
                      integration
                    </li>
                  </ol>
                </div>

                <div data-id="element-1423">
                  <h3 className="font-serif text-2xl text-stone-50 mb-4 flex items-center gap-3" data-id="element-1424">
                    <span className="font-mono text-sm bg-stone-800 px-2 py-1 rounded-sm" data-id="element-1425">
                      Phase 3
                    </span>
                    Scale (Weeks 13-20)
                  </h3>
                  <ol className="list-decimal list-inside space-y-2 ml-2" data-id="element-1426">
                    <li data-id="element-1427">
                      RateBrain v2 — dynamic pricing recommendations, win
                      probability scoring
                    </li>
                    <li data-id="element-1428">Multi-property portfolio management</li>
                    <li data-id="element-1429">
                      Agency network effects — cross-resort search and
                      comparison
                    </li>
                    <li data-id="element-1430">Negotiation copilot — AI-suggested counter-offers</li>
                    <li data-id="element-1431">
                      Advanced reporting — financial impact, savings
                      verification, ROI dashboards
                    </li>
                    <li data-id="element-1432">
                      API for PMS/CRM integrations (Opera, Salesforce, etc.)
                    </li>
                  </ol>
                </div>
              </div>
            </section>

            {/* Section 6 */}
            <section id="schema" className="scroll-mt-24" data-id="element-1433">
              <SectionHeader num="06" title="Data Model Overview" data-id="element-1434" />
              <p className="mb-6 text-lg" data-id="element-1435">
                Key entities for the database schema:
              </p>
              <ul className="space-y-3 list-disc list-inside ml-4" data-id="element-1436">
                <li data-id="element-1437">
                  <Code data-id="element-1438">Organization</Code> (resort group or agency, tenant
                  isolation)
                </li>
                <li data-id="element-1439">
                  <Code data-id="element-1440">Property</Code> (belongs to org, has rooms, rates,
                  policies)
                </li>
                <li data-id="element-1441">
                  <Code data-id="element-1442">User</Code> (belongs to org, has role)
                </li>
                <li data-id="element-1443">
                  <Code data-id="element-1444">RateSheet</Code> (belongs to property, seasonal rates,
                  room types, blackouts)
                </li>
                <li data-id="element-1445">
                  <Code data-id="element-1446">PolicyConfig</Code> (rate floors, concession limits,
                  approval thresholds)
                </li>
                <li data-id="element-1447">
                  <Code data-id="element-1448">Quote</Code> (the core entity — agency request → resort
                  response)
                </li>
                <li data-id="element-1449">
                  <Code data-id="element-1450">QuotePackage</Code> (Good/Better/Best options within a
                  quote)
                </li>
                <li data-id="element-1451">
                  <Code data-id="element-1452">Hold</Code> (inventory reservation with expiry)
                </li>
                <li data-id="element-1453">
                  <Code data-id="element-1454">Contract</Code> (generated from accepted quote)
                </li>
                <li data-id="element-1455">
                  <Code data-id="element-1456">Document</Code> (uploaded files for RAG — SOPs,
                  policies, templates)
                </li>
                <li data-id="element-1457">
                  <Code data-id="element-1458">AuditLog</Code> (every action tracked)
                </li>
              </ul>
            </section>

            {/* Section 7 */}
            <section id="ai" className="scroll-mt-24" data-id="element-1459">
              <SectionHeader num="07" title="AI Architecture & Implementation" data-id="element-1460" />

              <div className="space-y-8" data-id="element-1461">
                <Card title="RFP Parsing (Phase 1)" data-id="element-1462">
                  <p data-id="element-1463">
                    <strong data-id="element-1464">Input:</strong> Raw email text or form submission
                    from agency
                  </p>
                  <p data-id="element-1465">
                    <strong data-id="element-1466">Process:</strong> Claude extracts structured fields
                    (dates, room count, event types, budget signals, special
                    requests)
                  </p>
                  <p data-id="element-1467">
                    <strong data-id="element-1468">Output:</strong> Structured QuoteRequest object
                    ready for the quote engine
                  </p>
                  <p data-id="element-1469">
                    <strong data-id="element-1470">Prompt engineering:</strong> Few-shot examples from
                    real RFPs, structured output format
                  </p>
                </Card>

                <Card title="RAG Pipeline (Phase 2)" data-id="element-1471">
                  <p data-id="element-1472">
                    <strong data-id="element-1473">Document ingestion:</strong> Upload resort SOPs,
                    policies, rate logic → chunk → embed → store in vector DB
                  </p>
                  <p data-id="element-1474">
                    <strong data-id="element-1475">Query flow:</strong> User question → embed →
                    retrieve relevant chunks → Claude generates answer with
                    citations
                  </p>
                  <p data-id="element-1476">
                    <strong data-id="element-1477">Use cases:</strong> "What's our attrition policy for
                    groups over 50 rooms?", "What concessions can I offer for a
                    December wedding?"
                  </p>
                  <p className="text-amber-500 mt-2" data-id="element-1478">
                    <strong data-id="element-1479">Safety:</strong> AI never invents rates. All pricing
                    comes from rate sheets and policy configs.
                  </p>
                </Card>

                <Card title="RateBrain Intelligence (Phase 2-3)" data-id="element-1480">
                  <p data-id="element-1481">
                    <strong data-id="element-1482">Competitive monitoring:</strong> Track competitor
                    rates via rate shopping APIs
                  </p>
                  <p data-id="element-1483">
                    <strong data-id="element-1484">Demand signals:</strong> Historical booking
                    patterns, seasonality, lead time analysis
                  </p>
                  <p data-id="element-1485">
                    <strong data-id="element-1486">Recommendations:</strong> Suggested rate
                    adjustments, optimal package configurations
                  </p>
                  <p data-id="element-1487">
                    <strong data-id="element-1488">Constraint:</strong> All recommendations are bounded
                    by resort policy — AI suggests, rules decide
                  </p>
                </Card>
              </div>
            </section>

            {/* Section 8 */}
            <section id="api" className="scroll-mt-24" data-id="element-1489">
              <SectionHeader num="08" title="Key API Endpoints" data-id="element-1490" />
              <div className="space-y-4 font-mono text-sm" data-id="element-1491">
                <div data-id="element-1492">
                  <p className="text-stone-500 mb-1 uppercase tracking-widest text-xs" data-id="element-1493">
                    Auth
                  </p>
                  <p data-id="element-1494">
                    <Code data-id="element-1495">POST /api/auth/signup</Code>,{' '}
                    <Code data-id="element-1496">POST /api/auth/login</Code>,{' '}
                    <Code data-id="element-1497">GET /api/auth/me</Code>
                  </p>
                </div>
                <div data-id="element-1498">
                  <p className="text-stone-500 mb-1 uppercase tracking-widest text-xs" data-id="element-1499">
                    Properties
                  </p>
                  <p data-id="element-1500">
                    <Code data-id="element-1501">GET /api/properties</Code>,{' '}
                    <Code data-id="element-1502">POST /api/properties</Code>,{' '}
                    <Code data-id="element-1503">GET /api/properties/:id</Code>
                  </p>
                </div>
                <div data-id="element-1504">
                  <p className="text-stone-500 mb-1 uppercase tracking-widest text-xs" data-id="element-1505">
                    Rate Sheets
                  </p>
                  <p data-id="element-1506">
                    <Code data-id="element-1507">POST /api/properties/:id/rates</Code>,{' '}
                    <Code data-id="element-1508">PUT /api/rates/:id</Code>
                  </p>
                </div>
                <div data-id="element-1509">
                  <p className="text-stone-500 mb-1 uppercase tracking-widest text-xs" data-id="element-1510">
                    Quotes
                  </p>
                  <p data-id="element-1511">
                    <Code data-id="element-1512">POST /api/quotes</Code> (create from agency request),{' '}
                    <Code data-id="element-1513">GET /api/quotes</Code>,{' '}
                    <Code data-id="element-1514">GET /api/quotes/:id</Code>,{' '}
                    <Code data-id="element-1515">PUT /api/quotes/:id/respond</Code> (resort responds
                    with packages), <Code data-id="element-1516">POST /api/quotes/:id/hold</Code>{' '}
                    (agency places hold)
                  </p>
                </div>
                <div data-id="element-1517">
                  <p className="text-stone-500 mb-1 uppercase tracking-widest text-xs" data-id="element-1518">
                    Holds
                  </p>
                  <p data-id="element-1519">
                    <Code data-id="element-1520">GET /api/holds</Code>,{' '}
                    <Code data-id="element-1521">DELETE /api/holds/:id</Code> (release),{' '}
                    <Code data-id="element-1522">POST /api/holds/:id/convert</Code> (to contract)
                  </p>
                </div>
                <div data-id="element-1523">
                  <p className="text-stone-500 mb-1 uppercase tracking-widest text-xs" data-id="element-1524">
                    Documents
                  </p>
                  <p data-id="element-1525">
                    <Code data-id="element-1526">POST /api/documents/upload</Code> (for RAG),{' '}
                    <Code data-id="element-1527">GET /api/documents</Code>
                  </p>
                </div>
                <div data-id="element-1528">
                  <p className="text-stone-500 mb-1 uppercase tracking-widest text-xs" data-id="element-1529">
                    AI
                  </p>
                  <p data-id="element-1530">
                    <Code data-id="element-1531">POST /api/ai/parse-rfp</Code>,{' '}
                    <Code data-id="element-1532">POST /api/ai/ask</Code> (RAG query),{' '}
                    <Code data-id="element-1533">POST /api/ai/rate-suggestion</Code>
                  </p>
                </div>
                <div data-id="element-1534">
                  <p className="text-stone-500 mb-1 uppercase tracking-widest text-xs" data-id="element-1535">
                    Analytics
                  </p>
                  <p data-id="element-1536">
                    <Code data-id="element-1537">GET /api/analytics/overview</Code>,{' '}
                    <Code data-id="element-1538">GET /api/analytics/leakage</Code>
                  </p>
                </div>
              </div>
            </section>

            {/* Section 9 */}
            <section id="deployment" className="scroll-mt-24" data-id="element-1539">
              <SectionHeader num="09" title="Deployment Strategy" data-id="element-1540" />
              <ul className="space-y-3 list-disc list-inside ml-4 text-lg" data-id="element-1541">
                <li data-id="element-1542">
                  <strong data-id="element-1543">Primary:</strong> Vercel (frontend + API routes)
                </li>
                <li data-id="element-1544">
                  <strong data-id="element-1545">Database:</strong> Supabase (PostgreSQL + pgvector +
                  Auth + Storage)
                </li>
                <li data-id="element-1546">
                  <strong data-id="element-1547">CI/CD:</strong> GitHub → Vercel auto-deploy (preview
                  on PR, production on main)
                </li>
                <li data-id="element-1548">
                  <strong data-id="element-1549">Environments:</strong> Development (local), Staging
                  (Vercel preview), Production (Vercel)
                </li>
                <li data-id="element-1550">
                  <strong data-id="element-1551">Monitoring:</strong> Vercel Analytics + Sentry for
                  error tracking
                </li>
                <li data-id="element-1552">
                  <strong data-id="element-1553">Environment variables:</strong> Database URL,
                  Anthropic API key, Clerk keys, Resend API key, etc.
                </li>
              </ul>
            </section>

            {/* Section 10 */}
            <section id="workflow" className="scroll-mt-24" data-id="element-1554">
              <SectionHeader num="10" title="Development Workflow" data-id="element-1555" />
              <ul className="space-y-3 list-disc list-inside ml-4 text-lg" data-id="element-1556">
                <li data-id="element-1557">
                  <strong data-id="element-1558">AI-assisted development:</strong> Use Claude Code and
                  Cursor extensively. Write prompts, review output, iterate.
                </li>
                <li data-id="element-1559">
                  <strong data-id="element-1560">Branch strategy:</strong> <Code data-id="element-1561">main</Code>{' '}
                  (production), <Code data-id="element-1562">develop</Code> (staging),{' '}
                  <Code data-id="element-1563">feature/*</Code> branches
                </li>
                <li data-id="element-1564">
                  <strong data-id="element-1565">Code review:</strong> All PRs reviewed before merge.
                  AI-generated code gets extra scrutiny.
                </li>
                <li data-id="element-1566">
                  <strong data-id="element-1567">Communication:</strong> Daily async updates, weekly
                  sync calls
                </li>
                <li data-id="element-1568">
                  <strong data-id="element-1569">Documentation:</strong> Keep README updated, document
                  API endpoints, comment complex logic
                </li>
                <li data-id="element-1570">
                  <strong data-id="element-1571">Testing:</strong> Unit tests for business logic,
                  integration tests for API, E2E for critical flows
                </li>
              </ul>
            </section>

            {/* Section 11 */}
            <section id="metrics" className="scroll-mt-24" data-id="element-1572">
              <SectionHeader num="11" title="How We Measure Success" data-id="element-1573" />
              <ul className="space-y-3 list-disc list-inside ml-4 text-lg" data-id="element-1574">
                <li data-id="element-1575">
                  <strong data-id="element-1576">Quote velocity:</strong> Time from agency request to
                  quote delivery (target: under 60 seconds)
                </li>
                <li data-id="element-1577">
                  <strong data-id="element-1578">Conversion rate:</strong> Quotes → Holds → Contracts
                  (target: 2x improvement over email)
                </li>
                <li data-id="element-1579">
                  <strong data-id="element-1580">Leakage reduction:</strong> % of group revenue shifted
                  from wholesale to direct
                </li>
                <li data-id="element-1581">
                  <strong data-id="element-1582">Agency adoption:</strong> Number of active agencies
                  using the platform
                </li>
                <li data-id="element-1583">
                  <strong data-id="element-1584">System reliability:</strong> 99.9% uptime, sub-200ms
                  API response times
                </li>
              </ul>
            </section>

            {/* Section 12 */}
            <section id="getting-started" className="scroll-mt-24" data-id="element-1585">
              <SectionHeader num="12" title="Your First Week" data-id="element-1586" />
              <ol className="list-decimal list-inside space-y-3 ml-4 text-lg mb-12" data-id="element-1587">
                <li data-id="element-1588">Review this PRD thoroughly</li>
                <li data-id="element-1589">
                  Explore the prototype (all pages linked from the landing site)
                </li>
                <li data-id="element-1590">
                  Set up local development environment (clone repo, install
                  deps, run dev server)
                </li>
                <li data-id="element-1591">Set up Vercel project and connect GitHub repo</li>
                <li data-id="element-1592">Provision Supabase project (database + auth + storage)</li>
                <li data-id="element-1593">Get Anthropic API key for Claude integration</li>
                <li data-id="element-1594">Implement authentication (Clerk or Supabase Auth)</li>
                <li data-id="element-1595">Create database schema (Prisma migrations)</li>
                <li data-id="element-1596">
                  Build the first real API endpoint:{' '}
                  <Code data-id="element-1597">POST /api/quotes</Code>
                </li>
                <li data-id="element-1598">Deploy to staging and verify</li>
              </ol>

              <Callout data-id="element-1599">
                <strong data-id="element-1600">
                  Questions? Ideas? Disagreements with any of the above?
                </strong>{' '}
                Good. We want a developer who thinks critically and brings their
                own expertise. This PRD is a starting point, not a straitjacket.
                Let's build something great.
              </Callout>
            </section>
          </div>

          <footer className="mt-32 pt-8 border-t border-stone-800 text-center" data-id="element-1601">
            <p className="font-mono text-xs text-stone-600" data-id="element-1602">
              Built with Magic Patterns
            </p>
          </footer>
        </main>
      </div>
    </div>;
}